<template>
  <v-dialog v-model="dialog" fullscreen persistent scrollable>
    <v-card class="modern-dialog-card" elevation="24">
      <div class="base-dialog-header">
        <div class="header-left">
          <v-icon size="22" color="white" class="mr-2">mdi-engine-outline</v-icon>
          <h2 class="header-title">Add Engine</h2>
        </div>
        <div class="header-right">
          <v-btn icon small class="header-action-btn" @click="handleDialogStatus">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-form lazy-validation ref="engine-form" v-model="valid">
        <v-row no-gutters>
          <v-col cols="6">
            <v-card-text class="pt-4 pb-3" style="max-height: 70vh; overflow-y: auto;">
              <div class="mb-4">
                <label class="field-label">Engine Label <span class="required-star">*</span></label>
                <v-text-field
                  v-model="form.label"
                  outlined
                  dense
                  :rules="[rules.required, rules.specialChars]"
                  placeholder="Engine Label"
                  type="text"
                ></v-text-field>
              </div>
              <div class="mb-4">
                <label class="field-label">Image <span class="required-star">*</span></label>
                <v-text-field
                  v-model="selectedImage"
                  outlined
                  dense
                  placeholder="Image"
                  persistent-hint
                ></v-text-field>
              </div>
              <div class="mb-4">
                <label class="field-label">Container Name <span class="required-star">*</span></label>
                <v-text-field
                  v-model="form.containerName"
                  outlined
                  dense
                  :rules="[rules.required, rules.whiteSpace, rules.specialChars]"
                  placeholder="Container Name"
                ></v-text-field>
              </div>
              <div class="mb-4 d-flex align-center">
                <v-switch
                  v-model="isPython"
                  inset
                  label="Python Script"
                  class="mt-0 mr-6"
                  hide-details
                ></v-switch>
                <v-switch
                  v-model="form.requiresLlm"
                  inset
                  label="Requires LLM Model"
                  class="mt-0"
                  hide-details
                ></v-switch>
              </div>
              <div>
                <label class="field-label">Target Page <span class="optional-text">(optional)</span></label>
                <v-text-field
                  v-model="form.targetPage"
                  outlined
                  dense
                  :rules="[rules.specialChars]"
                  placeholder="Target Page"
                ></v-text-field>
              </div>
            </v-card-text>
          </v-col>

          <v-col cols="6">
            <v-card-text class="pt-4 pb-3" style="max-height: 70vh; overflow-y: auto;">
              <label class="field-label">Supported Devices</label>
              <engines-supported-devices
                :isEditProcess="false"
                :deviceModelsProp="form.supportedDevices"
                @emited-selected-device="updateJetsonDeviceMatrix"
              ></engines-supported-devices>
            </v-card-text>
          </v-col>
        </v-row>

        <v-card-actions class="modern-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text class="modern-cancel-btn" :disabled="formLoading" @click="handleDialogStatus">
            <v-icon left small>mdi-close</v-icon>
            Close
          </v-btn>
          <v-btn
            class="modern-save-btn"
            elevation="2"
            depressed
            @click="saveEngine"
            :disabled="!valid || formLoading"
            :loading="formLoading"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            Save Engine
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
import EnginesSupportedDevices from './EnginesSupportedDevices.vue'

export default {
  name: 'NewEngine',
  components: { EnginesSupportedDevices },
  computed: {
    isFilledAndHasNoSpecialChars () {
      return (
        Object.keys(this.selectedDevice).length > 0 &&
        this.selectedImage.length > 0 &&
        !/['ığüşöçĞÜŞÖÇİ]/g.test(this.scriptPath) &&
        !/['ığüşöçĞÜŞÖÇİ]/g.test(this.label) &&
        !/['ığüşöçĞÜŞÖÇİ]/g.test(this.containerName)
      )
    }
  },
  props: {
    dialog: {
      type: Boolean
    }
    // engine: { type: Object }
  },
  data () {
    return {
      data: {},
      icon: 'mdi-help-circle-outline',
      valid: true,
      formLoading: false,
      selectedDevice: {},
      selectedImage: '',
      isPython: false,
      form: {
        label: '',
        scriptPath: '/home/cli_client',
        containerName: '',
        targetPage: '',
        requiresLlm: false,
        supportedDevices: []
      },
      rules: {
        required: (v) => !!v || 'Field is required',
        whiteSpace: (v) =>
          !!v ||
          /^[a-zA-Z0-9_.-]*$/g.test(v) ||
          'Invalid characters. Only lowercase, uppercase, numbers and {.-_} are allowed.',
        specialChars: (value) => {
          // Turkish chars are restricted
          const regex = /['ığüşöçĞÜŞÖÇİ]/g
          return (
            !!value ||
            !regex.test(value) ||
            'Please give a name without special characters'
          )
        }
      }
    }
  },
  watch: {
    isPython (val) {
      this.form.scriptPath = val ? '/home/cli_client.py' : '/home/cli_client'
    }
  },
  methods: {
    handleDialogStatus () {
      this.$emit('dialog-status', false)
    },
    saveEngine () {
      if (this.$refs['engine-form'].validate()) {
        const data = {
          ...this.form,
          dockerUrl: this.selectedImage,
          isCustomStreamEngine: true
        }
        this.formLoading = true
        apiService
          .post('admin/engine', data)
          .then(() => {
            this.$emit('success')
            Object.keys(this.form).forEach((key) => {
              this.form[key] = null
            })
            this.selectedDevice = {}
            this.selectedImage = ''
            this.isPython = false
            Swal.fire({
              title: 'Successfully saved!',
              text: 'Engine has been successfully saved. Now you can start an inference with it.',
              icon: 'success'
            })
            this.handleDialogStatus()
            this.$refs['engine-form'].resetValidation()
          })
          .catch((error) => {
            Swal.fire({
              title: 'Engine Saved',
              text: `Engine save failed: ${error.response.data.message}`,
              icon: 'warning'
            })
          })
          .finally(() => {
            this.formLoading = false
          })
      }
    },
    updateJetsonDeviceMatrix (device) {
      this.form.supportedDevices = device
    }
  }
}
</script>

<style scoped>
.modern-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.base-dialog-header {
  background: linear-gradient(135deg, #285a48, #091413);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 10px 16px;
  min-height: 50px;
}

.header-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.header-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.header-action-btn {
  color: rgba(255, 255, 255, 0.85) !important;
  transition: background 0.2s ease;
}

.header-action-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  color: white !important;
}

.field-label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

.required-star {
  color: #f44336;
  margin-left: 2px;
}

.optional-text {
  font-weight: 400;
  color: #9e9e9e;
  font-size: 0.85rem;
}

.modern-dialog-actions {
  background: white !important;
  padding: 12px 16px !important;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.modern-cancel-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #7c8db0;
  border-radius: 8px !important;
  padding: 0 16px !important;
  height: 36px !important;
  min-width: 90px !important;
}

.modern-cancel-btn:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
  color: #2c3e50;
}

.modern-save-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 8px !important;
  padding: 0 20px !important;
  height: 36px !important;
  min-width: 130px !important;
  background: #285a48 !important;
  color: white !important;
}

.modern-save-btn:hover {
  transform: translateY(-1px);
}

.modern-save-btn:disabled {
  background: #c8dcf0 !important;
  color: #9e9e9e !important;
  box-shadow: none !important;
}
</style>
